import { useEffect, useMemo, useState } from 'react';
import {
  KAKEURI_CATEGORY_ID,
  getFuelPriceListRows,
  normalizeFuelSettings,
  resolveFuelDiscount,
  type FuelDiscount,
} from '@kawai/shared';
import { useAuth } from '../lib/auth.js';
import {
  subscribeFuelPrices,
  subscribeFuelPriceList,
  subscribeFuelSettings,
  type FuelPricesDoc,
  type FuelPriceListDoc,
  type FuelSettingsDoc,
} from '../lib/repositories/fuelPrices.js';

/**
 * F-11: 価格一覧表（A4 縦）。
 *
 * 旧 `as-takefu21ss/price-list.html` を React 化したもの。3 セクション:
 *   1. 一般価格      … fuel_prices/{tenantId}.* から計算（現金フリー / キャッシュレス / 会員）
 *   2. 社員価格      … fuel_price_list/{tenantId} の掛売以外の区分（F-16: 設定に連動）
 *   3. 掛売価格      … 掛売区分から計算（口座振替 / 振込 / 集金 +0）。掛売区分が無ければ非表示
 *
 * F-16: 区分と値引き幅は fuel_settings に連動する（doc 無しは従来ハードコード互換）。
 * 印刷ボタンは window.print() を呼ぶ。`@media print` でボタン非表示。
 *
 * S-136: 画面上の操作部（印刷ボタン / 警告）だけ v2（`ui-v2` を要素ごとに付ける）。一覧表本体
 * （`.sheet`）は印刷物なので書体・字間・色を変えない（App.tsx でも `UiV2Scope` に包まない）。
 * v2 の面はすべて `.no-print` の中にあり、印刷時にインクを使わない。用紙の影は元から
 * `@media print` で落としている。
 */

const FUELS: { key: 'haioku' | 'regular' | 'keiyu' | 'touyu'; label: string }[] = [
  { key: 'haioku', label: 'ハイオク' },
  { key: 'regular', label: 'レギュラー' },
  { key: 'keiyu', label: '軽油' },
  { key: 'touyu', label: '灯油' },
];

const STORE_NAME = 'アポロステーション 武生21SS';

interface Row {
  label: string;
  values: (number | null)[];
  highlight?: boolean;
}

function PriceCell({
  v,
  fuelKey,
}: {
  v: number | null;
  fuelKey: 'haioku' | 'regular' | 'keiyu' | 'touyu';
}) {
  const bg = {
    haioku: '#fff4e6',
    regular: '#ffefe8',
    keiyu: '#eaf6ec',
    touyu: '#e9f2fc',
  }[fuelKey];
  const fg = {
    haioku: '#a24200',
    regular: '#9c1b00',
    keiyu: '#006a0b',
    touyu: '#005691',
  }[fuelKey];
  return (
    <td
      style={{
        background: bg,
        color: fg,
        padding: '11px 6px',
        textAlign: 'center',
        borderBottom: '1px solid #e5e7eb',
        fontSize: '14pt',
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {v != null ? (
        <>
          {v}
          <span
            style={{
              fontSize: '8pt',
              fontWeight: 500,
              color: '#777',
              marginLeft: 2,
            }}
          >
            円/L
          </span>
        </>
      ) : (
        '―'
      )}
    </td>
  );
}

function GroupTable({
  title,
  rows,
  variant = 'default',
  labelColTitle,
}: {
  title: string;
  rows: Row[];
  variant?: 'default' | 'members' | 'kake';
  labelColTitle: string;
}) {
  const titleBorderColor = {
    default: '#1a73e8',
    members: '#6a4bb3',
    kake: '#b37a1f',
  }[variant];

  return (
    <section
      style={{
        marginBottom: '7mm',
        pageBreakInside: 'avoid',
      }}
    >
      <h2
        style={{
          display: 'inline-block',
          fontSize: '11pt',
          fontWeight: 700,
          color: '#1a1a1a',
          padding: '3px 14px 3px 10px',
          background: '#f0f3f7',
          borderLeft: `4px solid ${titleBorderColor}`,
          marginBottom: '2.5mm',
          letterSpacing: '0.06em',
        }}
      >
        {title}
      </h2>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                background: '#333',
                color: '#fff',
                textAlign: 'left',
                paddingLeft: 12,
                width: '36%',
                padding: '8px 6px',
                fontSize: '10.5pt',
                fontWeight: 800,
                letterSpacing: '0.04em',
              }}
            >
              {labelColTitle}
            </th>
            {FUELS.map((f) => {
              const bg = {
                haioku: '#e66000',
                regular: '#ce2800',
                keiyu: '#019413',
                touyu: '#007bc7',
              }[f.key];
              return (
                <th
                  key={f.key}
                  style={{
                    background: bg,
                    color: '#fff',
                    textAlign: 'center',
                    padding: '8px 6px',
                    fontSize: '10.5pt',
                    fontWeight: 800,
                    letterSpacing: '0.04em',
                    borderRight: '1px solid rgba(255,255,255,.3)',
                  }}
                >
                  {f.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              <td
                style={{
                  textAlign: 'left',
                  paddingLeft: row.highlight ? 8 : 12,
                  fontSize: '10.5pt',
                  fontWeight: row.highlight ? 800 : 600,
                  color: row.highlight ? '#000' : '#222',
                  background: row.highlight ? '#fff6d6' : '#f8f9fb',
                  whiteSpace: 'nowrap',
                  padding: '11px 6px',
                  borderBottom: idx === rows.length - 1 ? 'none' : '1px solid #e5e7eb',
                  borderLeft: row.highlight ? '4px solid #d99a00' : undefined,
                }}
              >
                {row.highlight ? (
                  <>
                    <span style={{ color: '#d99a00', marginRight: 4 }}>★</span>
                    {row.label}
                  </>
                ) : (
                  row.label
                )}
              </td>
              {FUELS.map((fuel, i) => (
                <PriceCell key={fuel.key} v={row.values[i] ?? null} fuelKey={fuel.key} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export default function PriceListPage() {
  const { tenantId } = useAuth();
  const [base, setBase] = useState<FuelPricesDoc | null>(null);
  const [list, setList] = useState<FuelPriceListDoc | null>(null);
  const [settingsDoc, setSettingsDoc] = useState<FuelSettingsDoc | null>(null);
  const [settingsError, setSettingsError] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string>('');

  useEffect(() => {
    if (!tenantId) return;
    const u1 = subscribeFuelPrices(tenantId, (data) => {
      setBase(data);
      setUpdatedAt(new Date().toLocaleString('ja-JP'));
    });
    const u2 = subscribeFuelPriceList(tenantId, (data) => {
      setList(data);
      setUpdatedAt(new Date().toLocaleString('ja-JP'));
    });
    const u3 = subscribeFuelSettings(tenantId, (data, error) => {
      // F-16 レビュー #10: 読取失敗は最後に取得できた設定を保持し、警告のみ出す
      // （null で上書きすると無警告でデフォルト値引きの印刷物が出てしまう）
      if (error) {
        setSettingsError(true);
        return;
      }
      setSettingsError(false);
      setSettingsDoc(data);
      setUpdatedAt(new Date().toLocaleString('ja-JP'));
    });
    return () => {
      u1();
      u2();
      u3();
    };
  }, [tenantId]);

  const settings = useMemo(() => normalizeFuelSettings(settingsDoc), [settingsDoc]);

  if (!tenantId) {
    return (
      <main className="ui-v2 p-10 text-body text-ink-2">
        <p>tenantId 不明: ログインしてください。</p>
      </main>
    );
  }

  const { pricing } = settings;
  const listRows = getFuelPriceListRows(list);

  // 一般価格: base から計算（値引き幅は F-16 設定に連動 / F-16b: 燃料別モード対応）
  const baseVals = (discount: FuelDiscount | null = null) =>
    FUELS.map((f) =>
      base?.[f.key] != null
        ? base![f.key] - (discount ? resolveFuelDiscount(discount, f.key) : 0)
        : null,
    );
  const generalRows: Row[] = [
    { label: '現金フリー', values: baseVals() },
    { label: 'キャッシュレス', values: baseVals(pricing.cashless) },
    {
      label: '会員価格',
      highlight: true,
      values: baseVals(pricing.member),
    },
  ];

  // 社員価格: 掛売以外の区分（F-16: 設定の区分・並び順に連動 / ラベルは区分名そのまま）
  const memberRows: Row[] = settings.categories
    .filter((c) => c.id !== KAKEURI_CATEGORY_ID)
    .map((c) => ({
      label: c.label,
      values: FUELS.map((f) => listRows[c.id]?.[f.key] ?? null),
    }));

  // 掛売価格: 掛売区分から計算（区分が無ければセクションごと非表示）
  const kakeuriCategory = settings.categories.find((c) => c.id === KAKEURI_CATEGORY_ID);
  const kakeVals = (discount: FuelDiscount | null = null) =>
    FUELS.map((f) => {
      const v = listRows[KAKEURI_CATEGORY_ID]?.[f.key];
      return v != null ? v - (discount ? resolveFuelDiscount(discount, f.key) : 0) : null;
    });
  const kakeRows: Row[] = [
    { label: '口座振替', values: kakeVals(pricing.kakeuriDirectDebit) },
    { label: '振込', values: kakeVals(pricing.kakeuriBank) },
    { label: '集金・店頭支払い', values: kakeVals() },
  ];

  return (
    <main>
      <style>{`
        @page { size: A4 portrait; margin: 0; }
        @media print {
          html, body, #root {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff;
            width: 100%;
            height: auto;
            min-height: 0;
          }
          .no-print { display: none !important; }
          .sheet-wrap {
            width: 210mm !important;
            height: 297mm !important;
            box-shadow: none !important;
            margin: 0 !important;
            aspect-ratio: auto !important;
          }
          .sheet {
            transform: none !important;
            position: static !important;
            width: 210mm !important;
            height: 297mm !important;
          }
          th, td, .group-title {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>

      {settingsError && (
        <div
          className="ui-v2 no-print mx-auto mt-4 rounded-lg bg-warning-tint px-[18px] py-3 text-label text-warning-ink"
          role="alert"
          style={{ width: 'min(210mm, calc(100vw - 32px))' }}
        >
          設定の取得に失敗しました。区分・値引き幅が最新でない可能性があります。ページを再読み込みしてから印刷してください。
        </div>
      )}

      <div
        className="ui-v2 no-print mx-auto mb-3.5 flex justify-end pt-5"
        style={{ width: 'min(210mm, calc(100vw - 32px))' }}
      >
        <button
          type="button"
          className="btn-print inline-flex items-center justify-center rounded-lg bg-brand px-5 py-2 text-body font-medium text-white hover:opacity-90"
          onClick={() => window.print()}
        >
          印刷
        </button>
      </div>

      <div
        className="sheet-wrap"
        style={{
          width: 'min(210mm, calc(100vw - 32px))',
          aspectRatio: '210 / 297',
          margin: '0 auto',
          position: 'relative',
          boxShadow: '0 6px 24px rgba(0,0,0,.14)',
          background: '#fff',
        }}
      >
        <div
          className="sheet"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '210mm',
            height: '297mm',
            padding: '14mm 14mm 10mm',
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
            transformOrigin: 'top left',
            fontFamily: '"Hiragino Sans", "Meiryo", sans-serif',
            color: '#1a1a1a',
          }}
        >
          <header
            style={{
              paddingBottom: '5mm',
              borderBottom: '2px solid #1a1a1a',
              marginBottom: '7mm',
            }}
          >
            <h1
              style={{
                fontSize: '26pt',
                fontWeight: 800,
                letterSpacing: '0.08em',
                lineHeight: 1.15,
              }}
            >
              価格一覧表
            </h1>
            <p
              style={{
                marginTop: '2mm',
                fontSize: '11pt',
                color: '#555',
                fontWeight: 500,
                letterSpacing: '0.04em',
              }}
            >
              {STORE_NAME}
            </p>
          </header>

          <GroupTable title="一般価格" rows={generalRows} labelColTitle="支払い方法" />
          {memberRows.length > 0 && (
            <GroupTable
              title="社員価格"
              rows={memberRows}
              variant="members"
              labelColTitle="区分"
            />
          )}
          {kakeuriCategory && (
            <GroupTable
              title="掛売価格"
              rows={kakeRows}
              variant="kake"
              labelColTitle="支払い方法"
            />
          )}

          <footer
            style={{
              marginTop: 'auto',
              paddingTop: '5mm',
              borderTop: '1px solid #e0e0e0',
              fontSize: '9pt',
              color: '#555',
              textAlign: 'right',
            }}
          >
            <p>取得日時: {updatedAt}</p>
          </footer>
        </div>
      </div>
    </main>
  );
}
